import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import ImageUpload from "../../components/ImageUpload";

export type ClubDetailsFormState = {
  name: string; description: string; physical_address: string; city: string; county: string;
  website: string; logo_url: string; brand_color: string; brand_color_secondary: string;
  gallery: string[]; sports: string[];
  legal_name: string; cui: string; registration_number: string; business_address: string;
  bank_account: string; bank_name: string;
};

/**
 * Controlled club details fields (public + business info, branding, gallery).
 * Presentational only — the parent (Settings) owns the state and the single
 * Save action, so this renders no card, heading, or save button of its own.
 *
 * Note: the club's sports are NOT edited here. They live in their own section
 * on the Settings page, which writes the club settings. This form used to
 * carry a second "Sporturi / Activități" picker that wrote a different column
 * in a different shape (names vs ids) — two sources of truth for one thing.
 */
/**
 * A titled block of fields.
 *
 * Declared at module scope, NOT inside the form component. Defined inline, it
 * was a brand-new function on every render, so React saw a different element
 * type each time and threw away the whole subtree instead of updating it —
 * which unmounted the input being typed into and dropped focus after every
 * single character.
 */
function Section({
  title,
  desc,
  children,
  divider = true,
}: {
  title: string;
  desc?: string;
  children: React.ReactNode;
  divider?: boolean;
}) {
  return (
    <section className={divider ? "pt-7 mt-7 border-t border-slate-100 first:pt-0 first:mt-0 first:border-0" : ""}>
      <h3 className="text-[13px] font-bold uppercase tracking-wider text-slate-500">{title}</h3>
      {desc && <p className="mt-1 text-sm text-slate-500">{desc}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

export default function ClubDetailsFields({
  form,
  setForm,
  canBrand,
}: {
  form: ClubDetailsFormState;
  setForm: (f: ClubDetailsFormState) => void;
  canBrand: boolean;
}) {
  const { t } = useTranslation();

  // Unique per mounted instance, so the ids below stay unique even if this
  // form is ever rendered twice on one page. Duplicate ids would silently
  // point every label at the first matching field.
  const uid = useId();
  const fid = (name: string) => `${uid}-${name}`;

  const input =
    "w-full px-3 py-2 text-sm transition-all bg-white border border-slate-300 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500";
  const label = "block mb-1.5 text-sm font-medium text-slate-700";
  const hint = "mt-1 text-xs text-slate-400";

  return (
    <div>
      {/* ---------- Identity: logo sits next to the name it belongs to ---------- */}
      <Section title={t("admin.identity", "Identitate")}>
        <div className="flex flex-col gap-6 sm:flex-row">
          <div className="shrink-0">
            <ImageUpload
              value={form.logo_url}
              onChange={(url) => setForm({ ...form, logo_url: url })}
              endpoint="club-logo"
              label={t("admin.clubLogo", "Logo club")}
              maxSizeMB={5}
              previewClassName="w-28 h-28"
            />
          </div>
          <div className="flex-1 min-w-0 space-y-4">
            <div>
              <label className={label} htmlFor={fid("name")}>
                {t("common.name", "Numele clubului")} <span className="text-rose-500">*</span>
              </label>
              <input
                id={fid("name")}
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={input}
              />
            </div>
            <div>
              <label className={label} htmlFor={fid("description")}>{t("common.description", "Descriere")}</label>
              <textarea
                id={fid("description")}
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={t("admin.descriptionPlaceholder", "Câteva rânduri despre club — pentru părinți și sportivi.")}
                className={`${input} resize-none`}
              />
            </div>
          </div>
        </div>
      </Section>

      {/* ---------- Contact ---------- */}
      <Section
        title={t("admin.contactSection", "Locație și contact")}
        desc={t("admin.contactSectionDesc", "Unde vă găsesc părinții și sportivii.")}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={label} htmlFor={fid("physical_address")}>{t("admin.physicalAddress", "Adresă fizică")}</label>
            <input
              id={fid("physical_address")}
              type="text"
              value={form.physical_address}
              onChange={(e) => setForm({ ...form, physical_address: e.target.value })}
              placeholder={t("admin.physicalAddressPlaceholder", "ex. Str. Sportului nr. 10")}
              className={input}
            />
          </div>
          <div>
            <label className={label} htmlFor={fid("city")}>{t("common.city", "Oraș")}</label>
            <input id={fid("city")} type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={input} />
          </div>
          <div>
            <label className={label} htmlFor={fid("county")}>{t("common.county", "Județ")}</label>
            <input id={fid("county")} type="text" value={form.county} onChange={(e) => setForm({ ...form, county: e.target.value })} className={input} />
          </div>
          <div className="sm:col-span-2">
            <label className={label} htmlFor={fid("website")}>{t("common.website", "Website")}</label>
            <input
              id={fid("website")}
              type="url"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="https://"
              className={input}
            />
          </div>
        </div>
      </Section>

      {/* ---------- Brand colours (Premium) ---------- */}
      <Section
        title={t("admin.brandColors", "Culori de brand")}
        desc={
          canBrand
            ? t("admin.brandColorsDesc", "Se aplică în aplicația sportivilor tăi.")
            : t("admin.brandColorsUpsell", "Personalizează culorile clubului în aplicație cu planul Premium.")
        }
      >
        {canBrand ? (
          <div className="flex flex-wrap gap-3">
            {([
              ["brand_color", t("admin.brandColorPrimary", "Culoare principală")],
              ["brand_color_secondary", t("admin.brandColorSecondary", "Culoare secundară")],
            ] as const).map(([field, lbl]) => (
              <div key={field} className="flex items-center gap-3 p-3 border rounded-xl border-slate-200 bg-slate-50/60">
                <input
                  type="color"
                  value={(form as any)[field] || "#4f46e5"}
                  onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                  className="w-11 h-11 border rounded-lg cursor-pointer border-slate-200 bg-white"
                  aria-label={lbl}
                />
                <div>
                  <div className="text-sm font-medium text-slate-700" id={fid(`${field}-label`)}>
                    {lbl}
                  </div>
                  {/* Named by the text above it: this box has no <label> of its
                      own, and the colour swatch already carries `lbl`. */}
                  <input
                    type="text"
                    aria-labelledby={fid(`${field}-label`)}
                    value={(form as any)[field] || ""}
                    onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                    placeholder="#4f46e5"
                    className="w-28 px-2 py-1 mt-1 font-mono text-xs border rounded border-slate-200 text-slate-700"
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <span className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700">
            Premium
          </span>
        )}
      </Section>

      {/* ---------- Gallery ---------- */}
      <Section
        title={t("admin.photoGallery", "Galerie foto")}
        desc={t("admin.photoGalleryDesc", "Fotografii cu facilitățile, evenimentele și activitățile clubului.")}
      >
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {form.gallery.map((url, index) => (
            <div key={index} className="relative overflow-hidden border group rounded-xl border-slate-200 bg-slate-100 aspect-video">
              {url ? (
                <img
                  src={url}
                  alt={`${t("admin.photoGallery", "Galerie")} ${index + 1}`}
                  className="object-cover w-full h-full"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    const placeholder = e.currentTarget.nextElementSibling;
                    if (placeholder) (placeholder as HTMLElement).style.display = "flex";
                  }}
                />
              ) : null}
              <div
                className="absolute inset-0 flex-col items-center justify-center hidden bg-slate-200"
                style={{ display: url ? "none" : "flex" }}
              >
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="mt-1 text-xs text-slate-500">{t("admin.imageUnavailable", "Imagine indisponibilă")}</span>
              </div>
              <button
                type="button"
                onClick={() => setForm({ ...form, gallery: form.gallery.filter((_, i) => i !== index) })}
                aria-label={t("common.delete", "Șterge")}
                className="absolute z-10 p-1.5 text-white transition-opacity rounded-lg opacity-0 top-2 right-2 bg-rose-500 group-hover:opacity-100 focus:opacity-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
          <ImageUpload
            value=""
            onChange={(url) => {
              if (url) setForm({ ...form, gallery: [...form.gallery, url] });
            }}
            endpoint="image"
            maxSizeMB={10}
            previewClassName="w-full aspect-video"
          />
        </div>
        <p className={hint}>{t("admin.galleryHint", "Format recomandat 16:9, maxim 10 MB per imagine.")}</p>
      </Section>

      {/* ---------- Business / billing ---------- */}
      <Section
        title={t("admin.businessInfo", "Date de facturare")}
        desc={t("admin.businessInfoDesc", "Aceste detalii apar pe facturile și documentele oficiale.")}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={label} htmlFor={fid("legal_name")}>{t("admin.legalName", "Denumire legală (nume firmă)")}</label>
            <input
              id={fid("legal_name")}
              type="text"
              value={form.legal_name}
              onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
              placeholder={t("admin.legalNamePlaceholder", "ex. SC Club Sportiv SRL")}
              className={input}
            />
          </div>
          <div>
            <label className={label} htmlFor={fid("cui")}>{t("admin.cui", "CUI (cod fiscal)")}</label>
            <input id={fid("cui")} type="text" value={form.cui} onChange={(e) => setForm({ ...form, cui: e.target.value })} placeholder="ex. RO12345678" className={input} />
          </div>
          <div>
            <label className={label} htmlFor={fid("registration_number")}>{t("admin.regNumber", "Număr de înregistrare")}</label>
            <input
              id={fid("registration_number")}
              type="text"
              value={form.registration_number}
              onChange={(e) => setForm({ ...form, registration_number: e.target.value })}
              placeholder="ex. J40/1234/2020"
              className={input}
            />
          </div>
          <div className="sm:col-span-2">
            <label className={label} htmlFor={fid("business_address")}>{t("admin.businessAddress", "Adresă sediu social")}</label>
            <input
              id={fid("business_address")}
              type="text"
              value={form.business_address}
              onChange={(e) => setForm({ ...form, business_address: e.target.value })}
              placeholder={t("admin.businessAddressPlaceholder", "ex. Str. Exemplu nr. 1, Sector 1, București")}
              className={input}
            />
          </div>
          <div>
            <label className={label} htmlFor={fid("bank_name")}>{t("admin.bankName", "Numele băncii")}</label>
            <input id={fid("bank_name")} type="text" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="ex. Banca Transilvania" className={input} />
          </div>
          <div>
            <label className={label} htmlFor={fid("bank_account")}>{t("admin.bankAccount", "IBAN / cont bancar")}</label>
            <input
              id={fid("bank_account")}
              type="text"
              value={form.bank_account}
              onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
              placeholder="ex. RO12BTRL1234567890123456"
              className={`${input} font-mono`}
            />
          </div>
        </div>
      </Section>
    </div>
  );
}
